// My Practice: the student's home in the student system. What their teachers have set
// (due soonest first, with attempts left), the consent screen when their school still needs
// it, their classes, and how a practice session works.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ban, CheckCircle2, GraduationCap, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { isOverdueUnfinished } from '../../utils/assignmentDue';
import { attemptsLeft, isOutOfAttempts } from '../../utils/classAttempts';
import { uiLocale } from '../../i18n/language';
import { MyAssignmentItem, getMyAssignments } from '../../services/assignmentService';
import { MyClasses, fetchMyClasses } from '../../services/classService';
import { ConsentCard } from './MyClassesPage';
import {
  cardStyle,
  errorStyle,
  hintStyle,
  pageStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
  subtitleStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('StudentHomePage');

const HOW_IT_WORKS = ['read', 'start', 'help', 'reflect'] as const;

/** The server's largest page; a student rarely has more than one. */
const PAGE_LIMIT = 200;
const MAX_PAGES = 10;

/** Every page of My Assignments. Class assignments are picked out here, so stopping at
 *  the first page could miss one behind a student's other assignments. */
async function allMyAssignments(token: string): Promise<MyAssignmentItem[]> {
  const items: MyAssignmentItem[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const result = await getMyAssignments(token, { page, limit: PAGE_LIMIT });
    items.push(...result.assignments);
    if (page >= result.pagination.total_pages) return items;
  }
  logger.warn(`Stopped after ${MAX_PAGES} pages of assignments`);
  return items;
}

function dueTime(item: MyAssignmentItem): number {
  return item.assignment.due_date ? new Date(item.assignment.due_date).getTime() : Number.POSITIVE_INFINITY;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  padding: '0.85rem 0',
  borderTop: `1px solid ${T.border}`,
};

const StudentHomePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<MyClasses | null>(null);
  const [assignments, setAssignments] = useState<MyAssignmentItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      const [mine, assigned] = await Promise.all([fetchMyClasses(), allMyAssignments(token)]);
      setClasses(mine);
      // Class assignments only: they are the ones that carry a teacher's rules.
      setAssignments(assigned.filter(a => a.rules));
    } catch (err) {
      logger.error('Failed to load student home', err);
      setError(t('studentHome.couldNotLoad'));
    }
  }, [token, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle} role="alert">{error}</div>
      </div>
    );
  }
  if (!classes || !assignments) return <LoadingIndicator message={t('studentHome.loading')} />;

  const needsConsent = classes.consent.required && !classes.consent.granted;
  const todo = assignments.filter(a => a.progress?.progress_status !== 'completed').sort((a, b) => dueTime(a) - dueTime(b));
  const done = assignments.filter(a => a.progress?.progress_status === 'completed');
  const studying = classes.classes.filter(c => c.role === 'student');

  const start = (item: MyAssignmentItem) =>
    navigate('/roleplay', {
      state: {
        preselectedAssignment: {
          assignmentId: item.assignment.id,
          bundleItems: item.bundle_items,
          assignmentTitle: item.assignment.title,
        },
      },
    });

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 id="tour-page-title" style={titleStyle}>
          {t('studentHome.title')}
        </h1>
        <p style={subtitleStyle}>{t('studentHome.subtitle')}</p>
      </div>

      {needsConsent && (
        <ConsentCard consent={classes.consent} onAccepted={consent => setClasses({ ...classes, consent })} />
      )}

      <section id="tour-page-primary-action" style={cardStyle} aria-labelledby="student-todo-heading">
        <h2 id="student-todo-heading" style={sectionHeadingStyle}>
          {t('studentHome.toDo')}
        </h2>
        {todo.length === 0 ? (
          <p style={{ ...hintStyle, margin: 0 }}>{t('studentHome.nothingToDo')}</p>
        ) : (
          todo.map(item => {
            const status = item.progress?.progress_status ?? 'not_started';
            const overdue = isOverdueUnfinished(item.assignment.due_date, status);
            const outOfAttempts = isOutOfAttempts(item);
            const blocked = needsConsent || overdue || outOfAttempts || item.content_deleted;
            const reason = needsConsent
              ? t('studentHome.consentFirst')
              : overdue
                ? t('myAssignments.overdueCannotStart')
                : outOfAttempts
                  ? t('myAssignments.noAttemptsLeftHint')
                  : undefined;
            return (
              <div key={item.assignment.id} style={rowStyle} data-testid="student-todo-row">
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: T.text }}>{item.assignment.title}</strong>
                  <p style={{ ...hintStyle, margin: '2px 0 0' }}>
                    {[
                      item.class_name,
                      item.rules?.practice_type ? t(`practiceTypes.${item.rules.practice_type}`) : null,
                      item.assignment.due_date
                        ? t('studentHome.due', {
                            date: new Date(item.assignment.due_date).toLocaleDateString(uiLocale(), {
                              month: 'short',
                              day: 'numeric',
                            }),
                          })
                        : t('assignmentRules.noDueDate'),
                      item.rules?.max_attempts
                        ? t('myAssignments.attemptsLeft', {
                            left: attemptsLeft(item) ?? item.rules.max_attempts,
                            total: item.rules.max_attempts,
                          })
                        : t('studentHome.unlimitedAttempts'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <span title={reason}>
                  <button
                    type="button"
                    style={blocked ? secondaryButtonStyle : primaryButtonStyle}
                    disabled={blocked}
                    onClick={() => start(item)}
                  >
                    {blocked ? <Ban size={14} /> : <Play size={14} />}
                    {outOfAttempts && !needsConsent && !overdue ? t('myAssignments.noAttemptsLeft') : t('myAssignments.start')}
                  </button>
                </span>
              </div>
            );
          })
        )}
        <p style={{ ...hintStyle, marginTop: '0.75rem' }}>
          <Link to="/my-assignments">{t('studentHome.allAssignments')}</Link>
        </p>
      </section>

      {done.length > 0 && (
        <section style={cardStyle} aria-labelledby="student-done-heading">
          <h2 id="student-done-heading" style={sectionHeadingStyle}>
            {t('studentHome.done', { count: done.length })}
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {done.map(item => (
              <li key={item.assignment.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', padding: '4px 0' }}>
                <CheckCircle2 size={16} color="#16a34a" /> {item.assignment.title}
              </li>
            ))}
          </ul>
          <p style={hintStyle}>
            <Link to="/sessions">{t('studentHome.seeFeedback')}</Link>
          </p>
        </section>
      )}

      <section id="tour-page-content" style={cardStyle} aria-labelledby="student-how-heading">
        <h2 id="student-how-heading" style={sectionHeadingStyle}>
          {t('studentHome.howItWorks')}
        </h2>
        <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', lineHeight: 1.7, color: T.text }}>
          {HOW_IT_WORKS.map(step => (
            <li key={step}>{t(`studentHome.steps.${step}`)}</li>
          ))}
        </ol>
      </section>

      {studying.length > 0 && (
        <section style={cardStyle} aria-labelledby="student-classes-heading">
          <h2 id="student-classes-heading" style={sectionHeadingStyle}>
            {t('classes.classesIAmIn')}
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {studying.map(c => (
              <span
                key={c.id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', padding: '4px 10px', borderRadius: 999, background: '#eff6ff', color: '#1e3a8a' }}
              >
                <GraduationCap size={14} /> {c.name}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default StudentHomePage;
